import type { ProfileData } from "@/features/candidate/lib/types";
import type { ProfileResult } from "@/lib/api/types";
import type { ProfileActivitySummary } from "@/lib/api/model";
import { detectHighlights } from "@/features/candidate/lib/highlights";

interface TransformProfileOptions {
  detectHighlights?: boolean;
}

export function transformProfile(
  p: ProfileResult,
  options: TransformProfileOptions = {},
): ProfileData {
  const experience: ProfileData["experience"] = (p.experiences ?? []).map((exp) => ({
    title: (exp.title_name as string) ?? (exp.title as string) ?? "",
    company: (exp.company_name as string) ?? "",
    company_domain: (exp.company_domain as string) ?? undefined,
    start_date: (exp.start_date as string) ?? undefined,
    end_date: (exp.end_date as string) ?? undefined,
    is_current: (exp.is_primary as boolean) ?? false,
  }));

  const topTitle = p.job_title ?? undefined;
  const topCompany = p.job_company_name ?? undefined;
  const hasCurrentRole = experience.some((entry) => entry.is_current);

  if ((topTitle || topCompany) && !hasCurrentRole) {
    experience.unshift({
      title: topTitle ?? "",
      company: topCompany ?? "",
      company_domain: p.job_company_domain ?? undefined,
      is_current: true,
    });
  }

  const education: ProfileData["education"] = (p.education ?? []).map((edu) => {
    const degrees = edu.degrees as string[] | undefined;
    const majors = edu.majors as string[] | undefined;
    const degree = degrees?.length ? degrees[0] : (edu.degree as string) ?? undefined;
    const field = majors?.length ? majors[0] : (edu.field as string) ?? (edu.major as string) ?? undefined;
    return {
      school: (edu.school_name as string) ?? "",
      school_domain: (edu.school_domain as string) ?? undefined,
      degree,
      field,
      start_date: (edu.start_date as string) ?? undefined,
      end_date: (edu.end_date as string) ?? undefined,
      is_current: (edu.is_current as boolean) ?? undefined,
    };
  });

  const profileData: ProfileData = {
    profile_id: p.profile_id,
    full_name: p.full_name ?? "Unknown",
    headline: p.headline ?? undefined,
    summary: p.summary ?? undefined,
    skills: p.skills ?? [],
    location: p.location_name ?? undefined,
    linkedin_url: p.linkedin_url ?? undefined,
    github_url: p.github_username ? `https://github.com/${p.github_username}` : undefined,
    // Public CDN URL — forwarded verbatim so CandidateAvatar can attempt the
    // fetch and silently fall back to initials on 404.
    avatar_url: p.avatar_url ?? null,
    social_profiles: (p.social_profiles ?? []) as unknown as ProfileData["social_profiles"],
    experience,
    education,
    badges: [],
    talent_pool: p.talent_pool ?? null,
    also_in_pools: p.also_in_pools ?? [],
    network_paths: p.network_paths ?? [],
    activity: (p.activity ?? null) as ProfileActivitySummary | null,
    criteria_score: p.criteria_score ?? null,
    criteria_band: p.criteria_band ?? null,
    criteria_results: p.criteria_results ?? [],
    viewed: p.viewed ?? false,
    viewed_at: p.viewed_at ?? null,
    review: p.review ?? null,
  };
  const highlights = (p as { highlights?: ProfileData["highlights"] }).highlights;
  if (Array.isArray(highlights)) {
    profileData.highlights = highlights;
  } else if (options.detectHighlights !== false) {
    profileData.highlights = detectHighlights(profileData);
  }
  return profileData;
}
